/**
 * Command Palette Hook
 *
 * Manages open/close state for the global command palette
 * and registers the Cmd+K / Ctrl+K keyboard shortcut.
 */

import { useCallback, useEffect, useState } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import type { SearchScope } from '../../components/features/search/SearchScopeFilter';

export function useCommandPalette() {
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();
  // Which scope the palette should land on when it opens. Null = its default.
  // Set by entry points that already know what the user is looking for — the
  // chat panel's "Search conversations" row opens straight into Chats rather
  // than making the user re-narrow.
  const [initialScope, setInitialScope] = useState<SearchScope | null>(null);

  // Track open event with source
  const trackOpen = useCallback((source: 'keyboard' | 'sidebar' | 'topbar') => {
    safeAnalytics.track(ANALYTICS_EVENTS.COMMAND_PALETTE_OPENED, {
      source,
    } as Record<string, unknown>);
  }, []);

  // Keyboard shortcut opens with 'keyboard' source
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (!isOpen) {
          trackOpen('keyboard');
          setInitialScope(null);
        }
        onToggle();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onToggle, isOpen, trackOpen]);

  // The nav rail's Search item. The `source: 'sidebar'` property value is
  // deliberately unchanged — renaming it would fork the funnel series.
  const openFromSidebar = useCallback(() => {
    trackOpen('sidebar');
    setInitialScope(null);
    onOpen();
  }, [onOpen, trackOpen]);

  // The workspace top bar's search bar, reached through CommandPaletteContext.
  const openFromTopBar = useCallback(() => {
    trackOpen('topbar');
    setInitialScope(null);
    onOpen();
  }, [onOpen, trackOpen]);

  /** Open pre-narrowed to a scope, from a surface that already knows the intent. */
  const openWithScope = useCallback(
    (scope: SearchScope, source: 'keyboard' | 'sidebar' = 'sidebar') => {
      trackOpen(source);
      setInitialScope(scope);
      onOpen();
    },
    [onOpen, trackOpen],
  );

  return {
    isOpen,
    onOpen: openFromSidebar,
    openFromTopBar,
    onClose,
    openWithScope,
    initialScope,
  };
}
